import { useEffect, useState } from 'react';
import { Box, CircularProgress, Drawer, IconButton, Link, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { leesJson } from '../../utils/apiFout';
import { buildGrippOfferUrl } from '../../utils/grippDeepLink';
import MarkdownPreview from './MarkdownPreview';
import { splitsOfferteNummers } from './offerteNummers';

/**
 * Snel in een eerdere offerte kijken zonder de generator te verlaten
 * (wiki-feedback Offerte generator, 26 sep: "eerdere offertes snel openen door erop
 * te klikken, dan kan je snel vergelijken"). Alleen lezen: onderwerp, klant,
 * regels met prijzen en de omschrijving; plus doorklik naar Gripp.
 */
const naamVan = (v) => (v && typeof v === 'object' ? (v.searchname || v.name || '') : String(v || ''));
const euro = (n) => `€ ${Number(n || 0).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function OfferteInkijk({ nummer, onClose }) {
  const [data, setData] = useState(null);
  const [fout, setFout] = useState('');
  useEffect(() => {
    if (!nummer) return undefined;
    let weg = false;
    setData(null); setFout('');
    (async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}/Api/offertes/offer?number=${encodeURIComponent(nummer)}`);
        const d = await leesJson(r, 'Offerte ophalen');
        if (!weg) setData(d);
      } catch (e) { if (!weg) setFout(e.message || 'Offerte niet gevonden'); }
    })();
    return () => { weg = true; };
  }, [nummer]);

  const offer = data?.offer;
  const regels = data?.lines || [];
  // Alleen het Gripp-totaal tonen (mét korting); zelf rekenen gaf een te hoog bedrag bij korting.
  const totaal = offer?.totaalExclBtw == null ? null : Number(offer.totaalExclBtw);
  const grippUrl = offer?.id ? buildGrippOfferUrl(offer.id) : null;

  return (
    <Drawer anchor="right" open={!!nummer} onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 520 }, p: 3 } }}>
      <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ mb: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary">Offerte {nummer}</Typography>
          <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.3 }}>{offer?.subject || (fout ? 'Niet gevonden' : '…')}</Typography>
          {offer && <Typography variant="body2" color="text.secondary">{naamVan(offer.company)}</Typography>}
        </Box>
        <IconButton size="small" onClick={onClose} aria-label="Sluiten"><CloseIcon fontSize="small" /></IconButton>
      </Stack>

      {!data && !fout && <CircularProgress size={20} />}
      {fout && <Typography variant="body2" color="text.secondary">{fout}</Typography>}

      {offer && (
        <>
          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            {grippUrl && <Link href={grippUrl} target="_blank" rel="noopener noreferrer" variant="body2">Open in Gripp ↗</Link>}
            {offer.viewUrl && <Link href={offer.viewUrl} target="_blank" rel="noopener noreferrer" variant="body2">Klantweergave ↗</Link>}
          </Stack>
          {regels.length > 0 && (
            <Box sx={{ mb: 2.5 }}>
              {regels.map((l, i) => (
                <Stack key={l.lineId || i} direction="row" spacing={1} sx={{ py: 0.6, borderBottom: '1px solid #f1f5f9' }}>
                  <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>{l.description}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>{l.amount} {l.unit}</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'nowrap', minWidth: 90, textAlign: 'right' }}>{euro(l.sellingprice)}</Typography>
                </Stack>
              ))}
              {totaal != null && Number.isFinite(totaal) && (
                <Stack direction="row" sx={{ pt: 0.75 }}>
                  <Typography variant="body2" fontWeight={700} sx={{ flex: 1 }}>Totaal excl. btw (Gripp)</Typography>
                  <Typography variant="body2" fontWeight={700}>{euro(totaal)}</Typography>
                </Stack>
              )}
            </Box>
          )}
          {offer.description && <MarkdownPreview text={offer.description} />}
        </>
      )}
    </Drawer>
  );
}

/** Tekst met klikbare offertenummers; klik = onOpen(nummer). */
export function TekstMetOffertes({ tekst, onOpen, render = (t) => t }) {
  const delen = splitsOfferteNummers(tekst);
  if (!onOpen || !delen.some((d) => d.nummer)) return render(tekst);
  return delen.map((d, i) => (d.nummer ? (
    <Link key={i} component="button" type="button" onClick={() => onOpen(d.nummer)} title="Bekijk deze offerte"
      sx={{ font: 'inherit', verticalAlign: 'baseline', color: 'inherit', fontWeight: 700, textDecorationColor: 'currentColor' }}>
      {d.nummer}
    </Link>
  ) : <span key={i}>{render(d.tekst)}</span>));
}
